'use client';

import type { Currency } from '@shared/utils';
import { useGetMatch, useGetPrize, useGetRank, useGetWinners } from '..';
import type { AdditionalDrawingInfo, OddsEntry } from '../../types';

type UseMapPrizeTablesMegaMillionsProps = {
  data: OddsEntry[];
  currency: Currency;
  namespace: [lotteryNamespace: string, generalNamespace: string];
  multiplier: number;
  additionalDrawingInfo: AdditionalDrawingInfo;
};

/**
 * Maps lottery odds data into a format suitable for displaying prize tables.
 *
 * @param data - The array of odds entries for the lottery and any extra games.
 * @param currency - The currency to display prize amounts in.
 * @param namespace - Tuple containing the lottery and general translation namespaces.
 * @param multiplier - Increase prize amount by set number and also filters winners from winnersByRankMultiplier
 * @param additionalDrawingInfo - Ovverides winners value for all ranks, byt ranl === 1.
 * @returns  An array of objects, each representing a prize table for a lottery or extra game.
 */
function useMapPrizeTablesMegaMillions({ data, currency, namespace, multiplier, additionalDrawingInfo }: UseMapPrizeTablesMegaMillionsProps) {
  const [lottery, general] = namespace;
  const getRank = useGetRank(lottery);
  const getMatch = useGetMatch(lottery);
  const getWinners = useGetWinners(general);
  const { megaMillions: getPrize } = useGetPrize(general);

  return (data ?? []).map(({ lotteryId, ranks }) => ({
    lotteryId,
    data: Object.entries(ranks ?? {}).map(([rank, conditions]) => {
      const winners: number | undefined = rank === '1' ? conditions[0]?.winners : additionalDrawingInfo!.winnersByRankMultiplier![rank]?.[multiplier];
      return {
        rank: getRank(Number(rank)),
        match: getMatch(conditions[0]!.matches),
        winners: getWinners(winners),
        amount: getPrize(conditions[0]?.prize?.[currency], rank, multiplier),
      };
    }),
  }));
}

export { useMapPrizeTablesMegaMillions };
